import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
	Box,
	Button,
	ColumnLayout,
	Container,
	Flashbar,
	FlashbarProps,
	Grid,
	Header,
	Icon,
	Input,
	Link,
	Modal,
	Popover,
	Select,
	SpaceBetween,
	Tabs,
} from '@amzn/awsui-components-console';
import {
	DndContext,
	DragEndEvent,
	DragOverEvent,
	DragOverlay,
	DragStartEvent,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
	AvailableField,
	LayoutField,
	LayoutSection,
	LayoutStep,
	PortalLayout,
} from './layoutSchema.types';

import {
	AutoPopulationDefinitionMap,
	getPlacedDependents,
	getUnmetDependencies,
} from './layoutDependencyUtils';
import LayoutPreview from './LayoutPreview';
import './LayoutEditor.scss';

interface LayoutEditorProps {
	workflowId: string;
	workflowName: string;
	/** All available fields extracted from the workflow's task definitions */
	availableFields: AvailableField[];
	workflowIntention?: string;
	/**
	 * AutoPopulation definitions keyed by clean attribute id. Used to enforce that
	 * first-level attributes (like EntityName) are placed before any attributes
	 * that depend on them.
	 */
	autoPopulationDefinitions?: AutoPopulationDefinitionMap;
	/**
	 * Layout loaded from the workflow definition API. If null/undefined, the editor
	 * starts with a default empty layout.
	 */
	initialLayout?: PortalLayout | null;
	openTools?: () => void;
	/**
	 * Persists the layout. Returning `{ ok: true }` shows a success flash;
	 * returning `{ ok: false, error }` shows an error flash and keeps the user
	 * on the editor page.
	 */
	onSave?: (layout: PortalLayout) => Promise<{ ok: true } | { ok: false; error: string }> | void;
	onUpdateDefinition?: () => void;
	onEditAttribute?: (attributeId: string, taskId: string | null, updates: Record<string, any>) => void;
	onCancel?: () => void;
}

interface SortableFieldProps {
	field: LayoutField;
	availableFields: AvailableField[];
	onRemove: (attributeId: string) => void;
	onToggleFullWidth: (attributeId: string) => void;
	/** Placed dependents that would break if this field were removed. */
	placedDependents: string[];
}

function SortableField({ field, availableFields, onRemove, onToggleFullWidth, placedDependents }: SortableFieldProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: field.attributeId,
	});

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const fieldMeta = availableFields.find(f => f.attributeId === field.attributeId);
	const removeDisabled = placedDependents.length > 0;

	return (
		<div ref={setNodeRef} style={style} className="layout-editor__field" {...attributes}>
			<div className="layout-editor__field-handle" {...listeners}>
				<Icon name="drag-indicator" />
			</div>
			<div className="layout-editor__field-content">
				<div>
					<Box variant="span" fontWeight="bold">{field.label || fieldMeta?.attributeName || field.attributeId}</Box>
					{fieldMeta?.dataType && (
						<Box variant="small" color="text-body-secondary"> ({fieldMeta.dataType})</Box>
					)}
					{fieldMeta?.reviewOnly && (
						<Box variant="small" color="text-status-warning"> [Review Only]</Box>
					)}
					{fieldMeta?.taskName && (
						<Box variant="small" color="text-status-info"> [{fieldMeta.taskName}]</Box>
					)}
				</div>
				{removeDisabled && (
					<Box variant="small" color="text-status-warning">
						<Icon name="status-warning" /> Cannot remove — {placedDependents.length === 1 ? 'field' : 'fields'} depending on this: {placedDependents.join(', ')}. Remove {placedDependents.length === 1 ? 'it' : 'them'} first.
					</Box>
				)}
			</div>
			<div className="layout-editor__field-actions">
				<Button
					variant="icon"
					iconName={field.fullWidth ? 'view-full' : 'view-horizontal'}
					onClick={() => onToggleFullWidth(field.attributeId)}
					ariaLabel={field.fullWidth ? 'Set half width' : 'Set full width'}
				/>
				<Button
					variant="icon"
					iconName="close"
					onClick={() => onRemove(field.attributeId)}
					disabled={removeDisabled}
					ariaLabel="Remove field"
				/>
			</div>
		</div>
	);
}

interface SectionEditorProps {
	section: LayoutSection;
	availableFields: AvailableField[];
	onUpdate: (section: LayoutSection) => void;
	onDelete: () => void;
	/** Map from field.attributeId to the list of placed dependents (empty = removable). */
	placedDependentsByFieldId: Record<string, string[]>;
}

function SectionEditor({ section, availableFields, onUpdate, onDelete, placedDependentsByFieldId }: SectionEditorProps) {
	const handleRemoveField = (attributeId: string) => {
		onUpdate({
			...section,
			fields: section.fields.filter(f => f.attributeId !== attributeId),
		});
	};

	const handleToggleFullWidth = (attributeId: string) => {
		onUpdate({
			...section,
			fields: section.fields.map(f =>
				f.attributeId === attributeId ? { ...f, fullWidth: !f.fullWidth } : f
			),
		});
	};

	const handleTitleChange = (title: string) => {
		onUpdate({ ...section, title });
	};

	const handleColumnsChange = (columns: string) => {
		onUpdate({ ...section, columns: parseInt(columns, 10) as 1 | 2 | 3 });
	};

	const fieldIds = section.fields.map(f => f.attributeId);

	return (
		<Container
			header={
				<Header
					actions={
						<SpaceBetween direction="horizontal" size="xs">
							<Select
								selectedOption={{ value: String(section.columns), label: `${section.columns} col` }}
								onChange={({ detail }) => handleColumnsChange(detail.selectedOption.value!)}
								options={[
									{ value: '1', label: '1 column' },
									{ value: '2', label: '2 columns' },
									{ value: '3', label: '3 columns' },
								]}
							/>
							<Button variant="icon" iconName="remove" onClick={onDelete} ariaLabel="Delete section" />
						</SpaceBetween>
					}
				>
					<Input
						value={section.title}
						onChange={({ detail }) => handleTitleChange(detail.value)}
						placeholder="Section title"
					/>
				</Header>
			}
		>
			<div className="layout-editor__section-body" data-section-id={section.id}>
				<SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
					{section.fields.length === 0 ? (
						<Box textAlign="center" color="text-body-secondary" padding="l">
							Drag fields here from the panel on the right
						</Box>
					) : (
						section.fields.map(field => (
							<SortableField
								key={field.attributeId}
								field={field}
								availableFields={availableFields}
								onRemove={handleRemoveField}
								onToggleFullWidth={handleToggleFullWidth}
								placedDependents={placedDependentsByFieldId[field.attributeId] || []}
							/>
						))
					)}
				</SortableContext>
			</div>
		</Container>
	);
}

export default function LayoutEditor({
	workflowId,
	workflowName,
	availableFields,
	workflowIntention,
	autoPopulationDefinitions,
	initialLayout,
	openTools,
	onSave,
	onUpdateDefinition,
	onEditAttribute,
	onCancel,
}: LayoutEditorProps) {
	const [layout, setLayout] = useState<PortalLayout>(() => initialLayout || {
		version: 1,
		workflowId,
		steps: [
			{
				id: 'step-1',
				title: 'Workflow Inputs',
				description: '',
				sections: [
					{ id: 'section-1', title: 'Common Fields', columns: 2, fields: [] },
				],
			},
		],
		lastModified: new Date().toISOString(),
	});
	const [isSaving, setIsSaving] = useState(false);

	const [activeStepIndex, setActiveStepIndex] = useState(0);
	const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);
	const [showAddStepModal, setShowAddStepModal] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [newStepTitle, setNewStepTitle] = useState('');
	const [newStepDescription, setNewStepDescription] = useState('');
	const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
	const [fieldSearchQuery, setFieldSearchQuery] = useState('');

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
	);

	const placedFieldIds = useMemo(() => {
		const ids = new Set<string>();
		layout.steps.forEach(step =>
			step.sections.forEach(section =>
				section.fields.forEach(field => ids.add(field.attributeId))
			)
		);
		return ids;
	}, [layout]);

	const unplacedFields = useMemo(
		() => availableFields.filter(f => !placedFieldIds.has(f.attributeId)),
		[availableFields, placedFieldIds]
	);

	// For each unplaced field, list the dependency attribute ids not yet placed.
	// Non-empty means the field is blocked from being added.
	const unmetDepsByFieldId = useMemo(() => {
		const map: Record<string, string[]> = {};
		unplacedFields.forEach(f => {
			map[f.attributeId] = getUnmetDependencies(f.attributeId, placedFieldIds, autoPopulationDefinitions);
		});
		return map;
	}, [unplacedFields, placedFieldIds, autoPopulationDefinitions]);

	// For each placed field, list the placed dependents that would break if it were removed.
	const placedDependentsByFieldId = useMemo(() => {
		const map: Record<string, string[]> = {};
		placedFieldIds.forEach(id => {
			map[id] = getPlacedDependents(id, placedFieldIds, autoPopulationDefinitions);
		});
		return map;
	}, [placedFieldIds, autoPopulationDefinitions]);

	const activeStep = layout.steps[activeStepIndex];

	const addStep = () => {
		if (!newStepTitle.trim()) return;
		const newStep: LayoutStep = {
			id: `step-${uuidv4()}`,
			title: newStepTitle.trim(),
			description: newStepDescription.trim() || '',
			sections: [{ id: `section-${uuidv4()}`, title: 'Fields', columns: 2, fields: [] }],
		};
		setLayout(prev => ({ ...prev, steps: [...prev.steps, newStep] }));
		setActiveStepIndex(layout.steps.length);
		setNewStepTitle('');
		setNewStepDescription('');
		setShowAddStepModal(false);
	};

	const removeStep = (index: number) => {
		if (layout.steps.length <= 1) return;
		setLayout(prev => ({
			...prev,
			steps: prev.steps.filter((_, i) => i !== index),
		}));
		setActiveStepIndex(Math.max(0, activeStepIndex - 1));
	};

	const updateStepTitle = (title: string) => {
		setLayout(prev => ({
			...prev,
			steps: prev.steps.map((s, i) => i === activeStepIndex ? { ...s, title } : s),
		}));
	};

	const updateStepDescription = (description: string) => {
		setLayout(prev => ({
			...prev,
			steps: prev.steps.map((s, i) => i === activeStepIndex ? { ...s, description } : s),
		}));
	};

	const addSection = () => {
		const newSection: LayoutSection = {
			id: `section-${uuidv4()}`,
			title: 'New Section',
			columns: 2,
			fields: [],
		};
		setLayout(prev => ({
			...prev,
			steps: prev.steps.map((step, i) =>
				i === activeStepIndex
					? { ...step, sections: [...step.sections, newSection] }
					: step
			),
		}));
	};

	const updateSection = (sectionIndex: number, updated: LayoutSection) => {
		setLayout(prev => ({
			...prev,
			steps: prev.steps.map((step, i) =>
				i === activeStepIndex
					? { ...step, sections: step.sections.map((s, j) => j === sectionIndex ? updated : s) }
					: step
			),
		}));
	};

	const deleteSection = (sectionIndex: number) => {
		setLayout(prev => ({
			...prev,
			steps: prev.steps.map((step, i) =>
				i === activeStepIndex
					? { ...step, sections: step.sections.filter((_, j) => j !== sectionIndex) }
					: step
			),
		}));
	};

	const handleDragStart = (event: DragStartEvent) => {
		setDraggedFieldId(event.active.id as string);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		setDraggedFieldId(null);
		const { active, over } = event;
		if (!over) return;

		const activeId = active.id as string;
		const overId = over.id as string;

		let sourceSectionIdx = -1;
		let sourceFieldIdx = -1;
		activeStep.sections.forEach((section, sIdx) => {
			const fIdx = section.fields.findIndex(f => f.attributeId === activeId);
			if (fIdx !== -1) {
				sourceSectionIdx = sIdx;
				sourceFieldIdx = fIdx;
			}
		});

		let targetSectionIdx = -1;
		let targetFieldIdx = -1;
		activeStep.sections.forEach((section, sIdx) => {
			const fIdx = section.fields.findIndex(f => f.attributeId === overId);
			if (fIdx !== -1) {
				targetSectionIdx = sIdx;
				targetFieldIdx = fIdx;
			}
		});

		if (sourceSectionIdx === -1 || targetSectionIdx === -1) return;

		if (sourceSectionIdx === targetSectionIdx) {
			const section = activeStep.sections[sourceSectionIdx];
			const newFields = arrayMove(section.fields, sourceFieldIdx, targetFieldIdx);
			updateSection(sourceSectionIdx, { ...section, fields: newFields });
		} else {
			const sourceSection = activeStep.sections[sourceSectionIdx];
			const targetSection = activeStep.sections[targetSectionIdx];
			const [movedField] = sourceSection.fields.splice(sourceFieldIdx, 1);

			const newTargetFields = [...targetSection.fields];
			newTargetFields.splice(targetFieldIdx, 0, movedField);

			const updatedSteps = layout.steps.map((step, i) => {
				if (i !== activeStepIndex) return step;
				return {
					...step,
					sections: step.sections.map((s, j) => {
						if (j === sourceSectionIdx) return { ...s, fields: sourceSection.fields };
						if (j === targetSectionIdx) return { ...s, fields: newTargetFields };
						return s;
					}),
				};
			});
			setLayout(prev => ({ ...prev, steps: updatedSteps }));
		}
	};

	const addFieldToSection = (attributeId: string, sectionIndex: number) => {
		// Enforce dependency graph: block adding a field until its dependencies are placed.
		const unmet = unmetDepsByFieldId[attributeId] || [];
		if (unmet.length > 0) {
			setFlashItems([{
				type: 'warning',
				content: `Add ${unmet.join(', ')} first — this field depends on ${unmet.length === 1 ? 'it' : 'them'}.`,
				dismissible: true,
				onDismiss: () => setFlashItems([]),
				id: `dep-block-${attributeId}`,
			}]);
			return;
		}

		// Leave `label` undefined so LayoutRenderer can derive the display name
		// (e.g. append the task name suffix for task-specific attributes).
		// Set `label` only if the user actually customizes it via the editor.
		const newField: LayoutField = {
			attributeId,
		};

		setLayout(prev => ({
			...prev,
			steps: prev.steps.map((step, i) =>
				i === activeStepIndex
					? {
						...step,
						sections: step.sections.map((s, j) =>
							j === sectionIndex ? { ...s, fields: [...s.fields, newField] } : s
						),
					}
					: step
			),
		}));
	};

	const validateLayout = (candidate: PortalLayout): string[] => {
		const errors: string[] = [];
		candidate.steps.forEach((step, sIdx) => {
			const stepLabel = step.title?.trim() || `Step ${sIdx + 1}`;
			if (step.sections.length === 0) {
				errors.push(`"${stepLabel}" has no sections — add a section or remove the step.`);
				return;
			}
			step.sections.forEach((section, secIdx) => {
				if (section.fields.length === 0) {
					const sectionLabel = section.title?.trim() || `Section ${secIdx + 1}`;
					errors.push(`Section "${sectionLabel}" in "${stepLabel}" has no fields — add at least one field or remove the section.`);
				}
			});
		});
		return errors;
	};

	const handleSave = async () => {
		const errors = validateLayout(layout);
		if (errors.length > 0) {
			setFlashItems([{
				type: 'error',
				header: 'Cannot save layout',
				content: (
					<ul style={{ margin: 0, paddingLeft: '20px' }}>
						{errors.map((err, i) => <li key={i}>{err}</li>)}
					</ul>
				),
				dismissible: true,
				onDismiss: () => setFlashItems([]),
				id: 'save-validation-error',
			}]);
			return;
		}

		if (!onSave) return;

		setIsSaving(true);
		try {
			const result = await onSave({ ...layout, lastModified: new Date().toISOString() });
			// void return counts as success for backward compatibility.
			if (!result || result.ok) {
				setFlashItems([{
					type: 'success',
					content: 'Layout saved successfully.',
					dismissible: true,
					onDismiss: () => setFlashItems([]),
					id: 'save-success',
				}]);
			} else {
				setFlashItems([{
					type: 'error',
					header: 'Failed to save layout',
					content: result.error || 'The workflow service rejected the update. Please try again.',
					dismissible: true,
					onDismiss: () => setFlashItems([]),
					id: 'save-error',
				}]);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const stepTabs = layout.steps.map((step, index) => ({
		id: String(index),
		label: step.title,
		content: null,
	}));

	return (
		<div className="layout-editor">
			<Flashbar items={flashItems} />
			<SpaceBetween size="l">
				<Header
					variant="h1"
					actions={
						<SpaceBetween direction="horizontal" size="s">
							<Button onClick={() => setShowPreview(true)} disabled={isSaving}>Preview</Button>
							<Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
							<Button variant="primary" onClick={handleSave} loading={isSaving}>Save Layout</Button>
						</SpaceBetween>
					}
					info={
						openTools && (
							<Link variant="info" onFollow={() => openTools()}>
								Info
							</Link>
						)
					}
					description={`Customize the requirement portal layout for "${workflowName}"`}
				>
					Layout Editor
				</Header>

				<Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
					<SpaceBetween size="l">
						<Container
							header={
								<Header
									actions={
										<SpaceBetween direction="horizontal" size="xs">
											<Button iconName="add-plus" onClick={() => setShowAddStepModal(true)}>
												Add Step
											</Button>
											{layout.steps.length > 1 && (
												<Button
													iconName="remove"
													onClick={() => removeStep(activeStepIndex)}
												>
													Remove Step
												</Button>
											)}
										</SpaceBetween>
									}
								>
									<Input
										value={activeStep?.title || ''}
										onChange={({ detail }) => updateStepTitle(detail.value)}
										placeholder="Step title"
									/>
								</Header>
							}
						>
							<SpaceBetween size="xs">
								<Input
									value={activeStep?.description || ''}
									onChange={({ detail }) => updateStepDescription(detail.value)}
									placeholder="Step description (optional)"
								/>
								<Tabs
								activeTabId={String(activeStepIndex)}
								onChange={({ detail }) => setActiveStepIndex(Number(detail.activeTabId))}
								tabs={stepTabs}
							/>
							</SpaceBetween>
						</Container>

						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragStart={handleDragStart}
							onDragEnd={handleDragEnd}
						>
							<SpaceBetween size="m">
								{activeStep?.sections.map((section, sIdx) => (
									<SectionEditor
										key={section.id}
										section={section}
										availableFields={availableFields}
										onUpdate={(updated) => updateSection(sIdx, updated)}
										onDelete={() => deleteSection(sIdx)}
										placedDependentsByFieldId={placedDependentsByFieldId}
									/>
								))}
							</SpaceBetween>

							<DragOverlay>
								{draggedFieldId ? (
									<div className="layout-editor__field layout-editor__field--dragging">
										<Icon name="drag-indicator" />
										<Box variant="span">
											{availableFields.find(f => f.attributeId === draggedFieldId)?.attributeName || draggedFieldId}
										</Box>
									</div>
								) : null}
							</DragOverlay>
						</DndContext>

						<Button iconName="add-plus" onClick={addSection}>
							Add Section
						</Button>
					</SpaceBetween>

					<Container header={<Header counter={`(${unplacedFields.length})`}>Available Fields</Header>}>
						<SpaceBetween size="s">
							<Input
								type="search"
								value={fieldSearchQuery}
								onChange={({ detail }) => setFieldSearchQuery(detail.value)}
								placeholder="Search fields..."
							/>
							{unplacedFields.length === 0 ? (
								<Box textAlign="center" color="text-body-secondary" padding="s">
									All fields have been placed
								</Box>
							) : (
								<SpaceBetween size="xs">
									{unplacedFields
										.filter(field => {
											if (!fieldSearchQuery.trim()) return true;
											const query = fieldSearchQuery.toLowerCase();
											return (
												field.attributeName.toLowerCase().includes(query) ||
												field.attributeId.toLowerCase().includes(query) ||
												(field.taskName?.toLowerCase().includes(query) ?? false) ||
												(field.dataType?.toLowerCase().includes(query) ?? false)
											);
										})
										.map(field => {
											const unmet = unmetDepsByFieldId[field.attributeId] || [];
											const isBlocked = unmet.length > 0;
											return (
												<div key={field.attributeId} className="layout-editor__palette-field">
													<SpaceBetween direction="horizontal" size="xs">
														<Box variant="span" fontWeight="bold">{field.attributeName}</Box>
														{field.reviewOnly && (
															<Box variant="small" color="text-status-warning">[Review Only]</Box>
														)}
														{field.taskName && (
															<Box variant="small" color="text-status-info">[{field.taskName}]</Box>
														)}
														{isBlocked && (
															<Popover
																size="small"
																position="top"
																triggerType="custom"
																dismissButton={false}
																content={
																	<Box variant="small">
																		Add {unmet.join(', ')} first — this field depends on {unmet.length === 1 ? 'it' : 'them'}.
																	</Box>
																}
															>
																<Box variant="small" color="text-status-warning">
																	<Icon name="status-warning" /> Locked
																</Box>
															</Popover>
														)}
													</SpaceBetween>
													<Box variant="small" color="text-body-secondary">{field.dataType}</Box>
													{activeStep?.sections.length > 0 && (
														<SpaceBetween direction="horizontal" size="xxs">
															{activeStep.sections.map((section, sIdx) => (
																<Button
																	key={section.id}
																	variant="inline-link"
																	disabled={isBlocked}
																	onClick={() => addFieldToSection(field.attributeId, sIdx)}
																>
																	+ {section.title}
																</Button>
															))}
														</SpaceBetween>
													)}
												</div>
											);
										})}
								</SpaceBetween>
							)}
						</SpaceBetween>
					</Container>
				</Grid>
			</SpaceBetween>

			<Modal
				visible={showAddStepModal}
				onDismiss={() => setShowAddStepModal(false)}
				header="Add New Step"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={() => setShowAddStepModal(false)}>Cancel</Button>
							<Button variant="primary" onClick={addStep} disabled={!newStepTitle.trim()}>
								Add
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="m">
					<Input
						value={newStepTitle}
						onChange={({ detail }) => setNewStepTitle(detail.value)}
						placeholder="Step title (e.g., Network Configuration)"
					/>
					<Input
						value={newStepDescription}
						onChange={({ detail }) => setNewStepDescription(detail.value)}
						placeholder="Step description (optional)"
					/>
				</SpaceBetween>
			</Modal>

			<Modal
				visible={showPreview}
				onDismiss={() => setShowPreview(false)}
				header="Portal Preview"
				size="max"
				footer={
					<Box float="right">
						<Button variant="primary" onClick={() => setShowPreview(false)}>Close Preview</Button>
					</Box>
				}
			>
				<LayoutPreview
					layout={layout}
					availableFields={availableFields}
					workflowName={workflowName}
					workflowIntention={workflowIntention}
				/>
			</Modal>
		</div>
	);
}
